import React from 'react';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@gw/ui';
import { ShieldAlert } from 'lucide-react';

import { useAuthStore, useOrganizationStore } from '@/stores/store';

import type { OrganizationRole } from '@gw/ui/types';

interface RoleGuardProps {
  children: React.ReactNode;
  /** Single role or array of roles - user needs ANY ONE of these roles for access */
  requiredRoles: OrganizationRole | OrganizationRole[];
  fallback?: React.ReactNode;
}

/**
 * RoleGuard - Protect routes based on user role
 * Checks both organization roles and global admin status
 * Accepts single role or array of roles (user needs any one of them)
 */
export function RoleGuard({ children, requiredRoles, fallback }: RoleGuardProps) {
  const { user } = useAuthStore();
  const { currentOrg } = useOrganizationStore();

  // Normalize to array for consistent handling
  const rolesArray = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

  // Check if user has global_admin role
  const isGlobalAdmin = user?.role === 'global_admin';

  // Check if user has ANY of the required organization roles
  const hasOrgRole = currentOrg?.roles?.some((role) => rolesArray.includes(role)) ?? false;

  // Global admins have access to everything
  const hasAccess = isGlobalAdmin || hasOrgRole;

  if (!hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="bg-destructive text-destructive-foreground flex h-10 w-10 items-center justify-center rounded-md">
                <ShieldAlert className="h-6 w-6" />
              </div>
              <div>
                <CardTitle>Access Denied</CardTitle>
                <CardDescription>
                  You don&apos;t have permission to access this page
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTitle>Required Role</AlertTitle>
              <AlertDescription>
                This page requires one of these roles: <strong>{rolesArray.join(', ')}</strong>.
                {currentOrg && (
                  <>
                    {' '}
                    Your current roles in <strong>{currentOrg.name}</strong> are:{' '}
                    <strong>{currentOrg.roles?.join(', ') || 'none'}</strong>.
                  </>
                )}
              </AlertDescription>
            </Alert>

            {rolesArray.includes('app_admin') && (
              <Alert>
                <AlertTitle>How to get access</AlertTitle>
                <AlertDescription>
                  The <strong>Application Admin</strong> role is required to manage applications.
                  Contact your organization administrator to have this role assigned to your
                  account. After the role is granted, log out and log back in for the change to take
                  effect.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end">
              <Button variant="outline" onClick={() => window.history.back()}>
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
